<script setup lang="ts">
import { useRouteQuery } from '@vueuse/router'
import type {
  ProductFilterTagWithCount,
  ProductSmartFilterDetail,
  ProductSetWithImageResponse,
} from '~/types/api'

const props = defineProps<{
  smartFilter?: ProductSmartFilterDetail | null
}>()

const selectedFilterTagsIds = ref<number[]>([])
const selectedFilterTags = computed(() => {
  const tags = filterTags.value.flatMap((category) =>
    category.collections.flatMap((collection) => collection.tags)
  )

  return tags
    .filter((tag) => selectedFilterTagsIds.value.includes(tag.nid))
    .reduce((acc, tag) => {
      if (acc.find((selectedTag) => selectedTag.nid === tag.nid)) {
        return acc
      }
      acc.push(tag)
      return acc
    }, [] as ProductFilterTagWithCount[])
})

// not using computed to avoid unwanted fetching
const activeTagsIds = ref<number[]>([])

const queryFilterTags = useRouteQuery('t[]', [], {
  transform: (val) => ([val].flat() || []).map((ft) => Number(ft)),
})

selectedFilterTagsIds.value = queryFilterTags.value
activeTagsIds.value = queryFilterTags.value.concat(
  props.smartFilter?.productFilterTags.map((tag) => tag.nid) || []
)

// debounced for fetching product sets
watchDebounced(
  [selectedFilterTagsIds],
  () => {
    firstLoad.value = true
    page.value = 1
    activeTagsIds.value = selectedFilterTagsIds.value.concat(
      props.smartFilter?.productFilterTags.map((tag) => tag.nid) || []
    )
    queryFilterTags.value = selectedFilterTagsIds.value
  },
  { debounce: 250, deep: true }
)

const { locale } = useI18n()
const filterTagRepository = createProductFilterTagRepository()

// TODO: should be one EP call
const { data: filterTags, status: filterTagsStatus } = await useLazyAsyncData(
  'filterTags_' + locale.value,
  async () => {
    const categories = await filterTagRepository.getAllCategories()

    const categoriesWithTags = await Promise.allSettled(
      categories.items.map(async (category) => {
        const collections = (
          await filterTagRepository.getAllCollections(category.nid)
        ).items

        const collectionsWithTags = await Promise.allSettled(
          collections.map(async (collection) => {
            const tags = (await filterTagRepository.getAllTags(collection.nid))
              .items as ProductFilterTagWithCount[]

            const tagsWithCount =
              await filterTagRepository.getCollectionTagsWithCount(
                collection.nid,
                activeTagsIds.value
              )

            tagsWithCount.forEach((tag) => {
              const tagIndex = tags.findIndex((t) => t.nid === tag.nid)
              if (tagIndex !== -1) {
                tags[tagIndex] = tag
              }
            })

            return {
              ...collection,
              tags,
            }
          })
        )

        return {
          ...category,
          collections: collectionsWithTags
            .filter((collection) => collection.status === 'fulfilled')
            .map((collection) => collection.value),
        }
      })
    )

    return categoriesWithTags
      .filter((category) => category.status === 'fulfilled')
      .map((category) => category.value)
  },
  {
    default: () => [],
  }
)

const { t } = useI18n()
const toast = useToast()

watch(
  activeTagsIds,
  async () => {
    const promises: Promise<ProductFilterTagWithCount[]>[] = []
    filterTags.value.forEach((category) => {
      category.collections.forEach((collection) => {
        promises.push(
          filterTagRepository.getCollectionTagsWithCount(
            collection.nid,
            activeTagsIds.value
          )
        )
      })
    })
    try {
      const tagsWithCount = await Promise.all(promises)
      let index = 0
      filterTags.value.forEach((category) => {
        category.collections.forEach((collection, collectionIndex) => {
          tagsWithCount[index + collectionIndex].forEach((tag) => {
            const tagIndex = collection.tags.findIndex((t) => t.nid === tag.nid)
            if (tagIndex !== -1) {
              collection.tags[tagIndex] = tag
            }
          })
        })
        index += category.collections.length
      })
    } catch (e) {
      toast.add({
        title: t('base.products.filters.errors.title'),
        description: t('base.products.filters.errors.failedToUpdateTagCounts'),
        color: 'red',
      })
    }
  },
  { deep: true }
)

const userStore = useUser()
const { isLoggedIn, userCustomerGroupId } = storeToRefs(userStore)

const userConfigStore = useUserConfig()
const { customerGroupId, priceCategoryId } = storeToRefs(userConfigStore)

const itemsPerPage = ref(54)
const page = useRouteQuery('page', 1, { transform: Number })
const route = useRoute()

const { data: setsData, status: setsStatus } = await useLazyAsyncData(
  'products_' + route.fullPath,
  () => {
    return createProductSetsRepository().getAllFilteredPagination({
      page: page.value,
      itemsPerPage: itemsPerPage.value,
      filterTagIds: activeTagsIds.value,
      customerGroupNid: customerGroupId.value ?? userCustomerGroupId.value,
      priceCategoryNid: priceCategoryId.value || undefined,
    })
  },
  {
    watch: [
      page,
      activeTagsIds,
      isLoggedIn,
      userCustomerGroupId,
      customerGroupId,
      priceCategoryId,
    ],
    default: defaultPaginationObject<ProductSetWithImageResponse>,
    deep: true,
  }
)

const sets = computed(() => setsData.value.items)

const gridSets = computed(() => {
  return transformSets(sets.value)
})

const firstLoad = ref(true)
const productSection = ref<HTMLElement | null>()
watch(sets, () => {
  if (firstLoad.value) {
    firstLoad.value = false
    return
  }
  productSection.value?.scrollIntoView({ behavior: 'smooth' })
})

const filtersPicker = useTemplateRef('filtersPicker')

function scrollToFilters() {
  filtersPicker.value?.scrollIntoView({ behavior: 'smooth' })
}

const pickerVisible = shallowRef(true)

const { stop } = useIntersectionObserver(
  filtersPicker,
  ([entry], observerElement) => {
    pickerVisible.value = entry?.isIntersecting || false
  }
)

onBeforeUnmount(() => {
  stop()
})
</script>

<template>
  <section>
    <div ref="productSection" class="bg-neutral-100 pb-24">
      <div class="container">
        <div ref="filtersPicker" class="max-h-screen overflow-y-auto">
          <div
            class="flex w-full flex-row pt-5"
            :class="{
              '!pb-0': filterTagsStatus === 'success' && !filterTags.length,
            }"
          >
            <div v-if="filterTagsStatus === 'pending'" class="w-full">
              <UiSkeletonFiltersPicker />
            </div>
            <div
              v-else-if="filterTagsStatus === 'error'"
              class="flex flex-col justify-center"
            >
              <span class="font-pragmatica-ext text-2xl font-bold">
                {{ $t('base.products.filters.errors.failedToLoadFilters') }}
              </span>
              <span class="text-md">
                {{ $t('base.products.filters.errors.unexpectedError') }}
              </span>
            </div>
            <ProductsFiltersPicker
              v-else
              v-model="selectedFilterTagsIds"
              :smart-filter-tags="props.smartFilter?.productFilterTags"
              :filter-tags="filterTags"
            />
          </div>
        </div>
        <div>
          <ProductsFiltersList
            class="pt-5 sm:hidden"
            v-model="selectedFilterTagsIds"
            :smart-filter="props.smartFilter"
            :selected-tags="selectedFilterTags"
          />
        </div>
        <div
          class="sticky top-0 z-10 -mb-1 border-b-4 border-white bg-neutral-100 py-5 transition-all duration-300"
        >
          <div class="flex flex-row flex-wrap items-center justify-end gap-2">
            <ProductsFiltersList
              class="max-sm:hidden"
              v-model="selectedFilterTagsIds"
              :smart-filter="props.smartFilter"
              :selected-tags="selectedFilterTags"
            />
            <div
              class="rounded-full bg-white px-4 py-1 text-sm text-neutral-600"
            >
              {{ setsData.itemCount }}
              {{ $t('base.products.filters.productCount') }}
            </div>
            <div>
              <UiButtonBlack
                :class="{ hidden: pickerVisible }"
                class="ml-auto"
                @click="scrollToFilters()"
              >
                {{ $t('base.products.filter') }}
              </UiButtonBlack>
            </div>
          </div>
        </div>
        <div v-if="setsStatus === 'pending'">
          <UiSkeletonProductsGrid v-for="i in 9" class="mb-1" />
        </div>
        <div
          v-else-if="setsStatus === 'error'"
          class="flex flex-col justify-center"
        >
          <span class="font-pragmatica-ext text-4xl font-bold">
            {{ $t('base.products.filters.errors.failedToLoadProducts') }}
          </span>
          <span class="text-md">
            {{ $t('base.products.filters.errors.unexpectedError') }}
          </span>
        </div>
        <div
          v-else-if="!sets.length"
          class="mt-10 flex flex-col justify-center"
        >
          <span class="font-pragmatica-ext text-4xl font-bold">
            {{ $t('base.products.filters.noProducts.title') }}
          </span>
          <span class="text-md">
            {{ $t('base.products.filters.noProducts.hint') }}
          </span>
        </div>
        <div v-else>
          <ProductsGrid :sets="gridSets" />
        </div>
        <div
          class="flex justify-end pr-6"
          :class="{
            'pt-5': setsData.itemCount > itemsPerPage,
          }"
        >
          <UiPagination
            v-model:value="page"
            :items-per-page="itemsPerPage"
            :item-count="setsData.itemCount"
            :loading="setsStatus === 'pending'"
          />
        </div>
      </div>
    </div>
  </section>
</template>
